import { useState, useEffect, useCallback } from 'react'
import { Prescription } from '../../../types/models/Presc'
import { AnnotationBox } from '../components/NumberAnnotationPanel'

type UseAnnotationStateParams = {
  /** 選択中の処方箋。null の場合は初期状態 */
  selectedPresc: Prescription | null
}

type UseAnnotationStateReturn = {
  annotations: AnnotationBox[]
  setAnnotations: (annotations: AnnotationBox[]) => void
  isKigouTwoColumn: boolean
  setIsKigouTwoColumn: (value: boolean) => void
  unifiedMedicalCode: string
  setUnifiedMedicalCode: (value: string) => void
}

/**
 * Annotator のアノテーション状態を管理するフック
 *
 * selectedPresc の id が変わった時にデフォルト値にリセットする。
 *
 * ## exhaustive-deps について
 *
 * useEffect の依存配列には selectedPresc オブジェクト全体ではなく、
 * 特定のプロパティ（id, basic_result）のみを含めている。
 *
 * 理由:
 * - selectedPresc は TanStack Query から返されるオブジェクトで、
 *   クエリの再取得時に新しい参照が生成される
 * - オブジェクト全体を依存に含めると、参照変更のたびに useEffect が再実行される
 * - 実際に必要なのは「処方箋が切り替わった時」のみ（id の変更）
 * - basic_result は統一医療機関コードの生成に使用するため含める
 *
 * @param params.selectedPresc - 選択中の処方箋
 */
export const useAnnotationState = ({
  selectedPresc,
}: UseAnnotationStateParams): UseAnnotationStateReturn => {
  const [annotations, setAnnotationsState] = useState<AnnotationBox[]>([])
  const [isKigouTwoColumn, setIsKigouTwoColumn] = useState(false)
  const [unifiedMedicalCode, setUnifiedMedicalCode] = useState('')

  // 依存値を明示的に抽出
  // オブジェクト参照ではなく、プリミティブ値で依存を管理
  const prescId = selectedPresc?.id
  const basicResult = selectedPresc?.basic_result

  // setAnnotations をラップして型を維持
  const setAnnotations = useCallback((newAnnotations: AnnotationBox[]) => {
    setAnnotationsState(newAnnotations)
  }, [])

  useEffect(() => {
    if (!selectedPresc || !prescId) {
      setAnnotationsState([])
      setIsKigouTwoColumn(false)
      setUnifiedMedicalCode('')
      return
    }

    // デフォルト値を設定
    setAnnotationsState([])
    setIsKigouTwoColumn(false)

    // 処方箋から統一医療機関コードを生成
    const placeCode =
      basicResult?.id_1_hospital_prefecture_code?.selected_option || ''
    const codeType = basicResult?.id_1_hospital_type_code?.selected_option || ''
    const hospitalCode = basicResult?.id_1_hospital_code?.selected_option || ''
    setUnifiedMedicalCode(`${placeCode}${codeType}${hospitalCode}`)
    // prescId は処方箋の切り替えを検出するために使用
    // basicResult は統一医療機関コードの生成に使用
    // selectedPresc オブジェクト全体を含めると、参照変更のたびに再実行されてしまう
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prescId, basicResult])

  return {
    annotations,
    setAnnotations,
    isKigouTwoColumn,
    setIsKigouTwoColumn,
    unifiedMedicalCode,
    setUnifiedMedicalCode,
  }
}
